import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Loader2 } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const InvoiceSchema = Yup.object().shape({
	detail: Yup.string().required("Descreva a cobrança"),
	value: Yup.number().min(0, "Valor inválido"),
});

const SITUACOES = [
	{ value: "open", label: "Em aberto" },
	{ value: "paid", label: "Paga" },
	{ value: "overdue", label: "Vencida" },
	{ value: "canceled", label: "Cancelada" },
];

const InvoiceModal = ({ open, onClose, invoiceId, companyId }) => {
	const initialState = {
		detail: "",
		value: 0,
		dueDate: "",
		status: "open",
		paymentMethod: "",
		companyId: companyId ? String(companyId) : "",
	};

	const [invoice, setInvoice] = useState(initialState);
	const [companies, setCompanies] = useState([]);

	useEffect(() => {
		(async () => {
			try {
				const { data } = await api.get("/companies");
				setCompanies(data.companies);
			} catch (err) {
				toastError(err);
			}
		})();
	}, [open]);

	useEffect(() => {
		(async () => {
			if (!invoiceId) return;
			try {
				const { data } = await api.get(`/invoices/${invoiceId}`);
				setInvoice((anterior) => ({
					...anterior,
					...data,
					paymentMethod: data.paymentMethod || "",
					companyId: String(data.companyId),
					dueDate: data.dueDate ? String(data.dueDate).slice(0, 10) : "",
				}));
			} catch (err) {
				toastError(err);
			}
		})();

		return () => setInvoice(initialState);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [invoiceId, open]);

	const handleClose = () => {
		onClose();
		setInvoice(initialState);
	};

	const handleSave = async (values) => {
		const payload = {
			...values,
			companyId: Number(values.companyId),
			dueDate: values.dueDate || null,
		};

		try {
			if (invoiceId) {
				await api.put(`/invoices/${invoiceId}`, payload);
			} else {
				await api.post("/invoices", payload);
			}
			toast.success("Fatura salva.");
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(aberto) => !aberto && handleClose()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>
						{invoiceId ? "Editar fatura" : "Nova fatura"}
					</DialogTitle>
				</DialogHeader>
				<Formik
					initialValues={invoice}
					enableReinitialize
					validationSchema={InvoiceSchema}
					onSubmit={(values, actions) => {
						handleSave(values);
						actions.setSubmitting(false);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form className="space-y-4">
							<div className="space-y-1.5">
								<Label>Empresa</Label>
								<Select
									value={values.companyId}
									onValueChange={(valor) => setFieldValue("companyId", valor)}
								>
									<SelectTrigger>
										<SelectValue placeholder="Selecione a empresa" />
									</SelectTrigger>
									<SelectContent>
										{companies.map((company) => (
											<SelectItem key={company.id} value={String(company.id)}>
												{company.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="detail">Descrição</Label>
								<Field
									as={Input}
									id="detail"
									name="detail"
									placeholder="Mensalidade 07/2026"
									autoFocus
								/>
								{touched.detail && errors.detail && (
									<p className="text-xs text-destructive">{errors.detail}</p>
								)}
							</div>

							<div className="grid gap-3 sm:grid-cols-2">
								<div className="space-y-1.5">
									<Label htmlFor="value">Valor (R$)</Label>
									<Field
										as={Input}
										id="value"
										name="value"
										type="number"
										step="0.01"
										min="0"
									/>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="dueDate">Vencimento</Label>
									<Field as={Input} id="dueDate" name="dueDate" type="date" />
								</div>
							</div>

							<div className="grid gap-3 sm:grid-cols-2">
								<div className="space-y-1.5">
									<Label>Situação</Label>
									<Select
										value={values.status}
										onValueChange={(valor) => setFieldValue("status", valor)}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{SITUACOES.map((item) => (
												<SelectItem key={item.value} value={item.value}>
													{item.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="paymentMethod">Forma de pagamento</Label>
									<Field
										as={Input}
										id="paymentMethod"
										name="paymentMethod"
										placeholder="PIX, boleto..."
									/>
								</div>
							</div>

							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={handleClose}
									disabled={isSubmitting}
								>
									Cancelar
								</Button>
								<Button type="submit" disabled={isSubmitting}>
									{isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
									Salvar
								</Button>
							</DialogFooter>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

export default InvoiceModal;
